import Icon from '@/components/icons/Icon';
import { classnames } from '@/helpers';
import { isDealsSearch, SearchType } from '@/hooks/search/useSearch.helpers';
import { _t } from '@/locale';
import { TranslationKey } from '@/locale/translation-map';
import { Fragment } from 'react/jsx-runtime';

const baseTranslationKey = 'features.searchV2.components.MatchingSearchResultsCount' as const;

const getHitsTranslationKey = (type: SearchType): TranslationKey => {
  switch (type) {
    case 'sistaminuten':
      return `${baseTranslationKey}.sistaminutenLegacy.hits`;
    case 'deals_sistaminuten':
      return `${baseTranslationKey}.sistaminuten.hits`;
    case 'deals':
    case 'deals_preview':
    case 'brand_deals':
      return `${baseTranslationKey}.deals.hits`;
    default:
      return `${baseTranslationKey}.hits`;
  }
};

export const MatchingSearchResultsCount = ({
  type,
  results,
  loading,
}: {
  type: SearchType;
  results: number;
  loading: boolean;
}) => {
  /**
   * show mirrored skeleton when loading to avoid layout shift
   */
  if (loading) {
    const gradientBase = 'bg-surface_l3 animate-pulse';
    return (
      <div className="space-x-sm flex w-full items-center" role="status" aria-live="polite">
        <div
          aria-hidden="true"
          className={classnames(gradientBase, 'h-xl w-xl rounded')}
          style={{ height: 24, width: 24 /** hight and width of icon */ }}
        />
        <div
          aria-hidden="true"
          className={classnames(gradientBase, 'rounded')}
          style={{ width: 200, height: 18 /** height of text-m */ }}
        />
        <span className="sr-only">{_t(`${baseTranslationKey}.loadingSearchResults`)}</span>
      </div>
    );
  }

  /**
   * Only show the zero count copy for deals searches
   */
  if (results < 1 && !isDealsSearch(type)) {
    return null;
  }

  const hitsTranslationKey = getHitsTranslationKey(type);
  const translation = _t(hitsTranslationKey, { count: results }) as string;

  return (
    <Fragment>
      <Icon variant="search-check" color="fg_secondary" size="sm" style={{ width: 24, height: 24 }} />
      <span className="text-fg_secondary text-m">{translation}</span>
    </Fragment>
  );
};
